import React, { useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { COLORS, Row, Col, Separator } from '@cuemath/comps';
import { Text, Button } from '@cuemath/web-comps';

import { openModal } from 'actions/modal';
import { MODAL_FORM } from 'constants/form';
import MODAL from 'constants/modal';

const PAYMENT_TYPE_LABEL = { SUBSCRIPTION: 'Subscription', ONE_TIME: 'One-time' };
const GREY = COLORS.GREY_2 || '#666';
const LINE = COLORS.GERANIUM || '#e6e6e6';
const CTA = COLORS.CTA_LINK || '#1a73e8';

const Panel = styled(Col)`
  background: ${COLORS.WHITE};
  border: 1px solid ${LINE};
  border-radius: 8px;
  padding: 16px 20px;
`;

const Title = styled(Text)`
  && {
    font-weight: 700;
  }
`;

const Blurb = styled(Text)`
  color: ${GREY};
`;

const GuideLink = styled.span`
  color: ${CTA};
  font-weight: 600;
  cursor: pointer;
  display: inline-block;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

// Clean hairline delimiter (replaces the odd grey band a visible Separator drew).
const Divider = styled.div`
  height: 1px;
  background: ${LINE};
  margin: 14px 0;
`;

const ChipsRow = styled(Row)`
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const DefChip = styled(Row)`
  gap: 6px;
  align-items: center;
  background: ${COLORS.BACKGROUND || '#f5f5f5'};
  border-radius: 6px;
  padding: 6px 10px;
`;

const EditButton = styled(Button)`
  && {
    flex: 0 0 auto;
  }
`;

const FreqLabel = styled(Text)`
  color: ${GREY};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

// Segmented 2CPW / 3CPW toggle — mirrors how the parent picks frequency on leap.
const FreqTabs = styled(Row)`
  gap: 8px;
  flex-wrap: wrap;
`;

const FreqTab = styled.button`
  border: 1px solid ${p => (p.$on ? CTA : LINE)};
  background: ${p => (p.$on ? 'rgba(26,115,232,0.08)' : COLORS.WHITE)};
  color: ${p => (p.$on ? CTA : COLORS.TEXT || '#222')};
  border-radius: 999px;
  padding: 6px 14px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
`;

const CardsRow = styled(Row)`
  flex-wrap: wrap;
  gap: 12px;
`;

const PlanCard = styled(Col)`
  min-width: 168px;
  flex: 1 1 168px;
  max-width: 240px;
  border: 1px solid ${LINE};
  border-radius: 10px;
  padding: 12px 14px;
  gap: 3px;
`;

const Tenure = styled(Text)`
  color: ${GREY};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PriceLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
  white-space: nowrap;
`;

const SavingsBadge = styled(Text)`
  color: ${COLORS.GREEN_2 || '#1a7f37'};
`;

const savingsFor = plan => {
  const pct =
    plan.annual_vs_monthly_discount_percentage ??
    plan.semi_annual_vs_monthly_discount_percentage ??
    plan.quarterly_vs_monthly_discount_percentage;
  const amt =
    plan.annual_savings_amount ??
    plan.semi_annual_savings_amount ??
    plan.quarterly_savings_amount;
  return { pct, amt };
};

function Definition({ label, value }) {
  return (
    <DefChip>
      <Text variant="caption" color={GREY}>
        {label}
      </Text>
      <Text variant="body2Bold">{value}</Text>
    </DefChip>
  );
}

function PlanCards({ plans, symbol }) {
  return (
    <CardsRow>
      {plans.map(plan => {
        const { pct, amt } = savingsFor(plan);
        return (
          <PlanCard key={plan.id}>
            <Tenure variant="caption">{plan.type}</Tenure>
            <PriceLine>
              <Text variant="body1Bold">
                {symbol}
                {plan.price_per_session}
              </Text>
              <Text variant="caption" color={GREY}>
                /session
              </Text>
            </PriceLine>
            <Text variant="body2" color={GREY}>
              {plan.classes} sessions · {symbol}
              {plan.price} total
            </Text>
            {pct ? (
              <SavingsBadge variant="caption">
                Save {pct}%{amt ? ` (${symbol}${amt})` : ''}
              </SavingsBadge>
            ) : null}
          </PlanCard>
        );
      })}
    </CardsRow>
  );
}

function PaymentFlowOverridesPanel({ viewData, onEdit }) {
  const dispatch = useDispatch();
  const plansEnvelope = viewData?.plans || {};
  const plans = Array.isArray(plansEnvelope.plans) ? plansEnvelope.plans : [];
  const paymentType =
    PAYMENT_TYPE_LABEL[plansEnvelope.payment_mode] || plansEnvelope.payment_mode || '-';
  const defaults = viewData?.effective_defaults || {};
  const symbol = plans[0]?.currency_symbol || viewData?.currency || '';

  // Group by classes-per-week. The catalog usually offers a 2/week and a 3/week
  // variant, but either may be missing and a group may hold 0-3 plans — so the
  // frequency toggle is built from whatever frequencies actually came back.
  const byFreq = {};
  plans.forEach(p => {
    const f = p.classes_per_week || 0;
    (byFreq[f] = byFreq[f] || []).push(p);
  });
  const freqs = Object.keys(byFreq)
    .map(Number)
    .sort((a, b) => a - b);

  const [selFreq, setSelFreq] = useState(null);
  // Fall back to the first available frequency if nothing is selected yet or the
  // selection is no longer present (e.g. after an override flips the catalog).
  const activeFreq = selFreq != null && byFreq[selFreq] ? selFreq : freqs[0];

  const openGuide = () =>
    dispatch(
      openModal(
        MODAL_FORM.PAYMENT_FLOW_GUIDE,
        { size: 'custom', width: '860px', height: MODAL.HEIGHT },
        {},
      ),
    );

  return (
    <Panel>
      <Col>
        <Title variant="body1Bold">Payment setup (Admissions)</Title>
        <Separator height={4} transparent />
        <Blurb variant="body2">
          These are the exact plans this parent sees on the leap platform — they
          pay online there (India defaults to one-time; elsewhere subscription),
          so subscription-link generation is off here. For an offline course,
          generate a one-time payment link instead.
        </Blurb>
        <Separator height={6} transparent />
        <GuideLink onClick={openGuide}>Full guide ↗</GuideLink>
      </Col>

      <Divider />

      <Row justifyContent="space-between" alignItems="center" gap={12}>
        <ChipsRow>
          <Definition label="Payment type" value={paymentType} />
          <Definition
            label="Enrollment type"
            value={plans[0]?.enrollment_type || defaults.plan_tier || '-'}
          />
          <Definition
            label="Gateway"
            value={plans[0]?.payment_client || defaults.payment_gateway || 'default'}
          />
          <Definition label="Grade" value={viewData?.grade ?? '-'} />
        </ChipsRow>
        {onEdit && (
          <EditButton
            size="xsmall"
            variant="secondary"
            label="Edit payment setup"
            onClick={onEdit}
          />
        )}
      </Row>

      <Separator height={14} transparent />
      <Text variant="body2Bold">{plansEnvelope.session_duration_text || 'Plans'}</Text>
      <Separator height={10} transparent />

      {plans.length ? (
        <>
          {freqs.length > 1 ? (
            <FreqTabs>
              {freqs.map(freq => (
                <FreqTab
                  key={freq}
                  type="button"
                  $on={activeFreq === freq}
                  onClick={() => setSelFreq(freq)}
                >
                  {freq} sessions / week
                </FreqTab>
              ))}
            </FreqTabs>
          ) : (
            <FreqLabel variant="caption">{activeFreq} sessions / week</FreqLabel>
          )}
          <Separator height={10} transparent />
          <PlanCards plans={byFreq[activeFreq] || []} symbol={symbol} />
        </>
      ) : (
        <Text variant="body2" color={GREY}>
          No plans returned for this student.
        </Text>
      )}
    </Panel>
  );
}

export default PaymentFlowOverridesPanel;
